import { Notification } from 'element-ui'

import {
  getUnreadAlarms,
  getUnreadDeviceStatusNotifications,
  markAllAlarmsRead,
  markAllDeviceStatusNotificationsRead,
} from '@/api/projectOrEquipment.js'
import {
  ALARM_EVENT_TYPE,
  DEVICE_STATUS_EVENT_TYPE,
  NOTIFICATION_CATEGORY,
} from '@/config/enums.js'

const POLLING_INTERVAL = 5000
const MAX_VISIBLE_NOTIFICATIONS = 50

const settle = (promise) => promise
  .then(response => ({ response }))
  .catch(() => ({ response: null }))

export default {
  data() {
    return {
      showNotificationPanel: false,
      notificationPollingTimer: null,
      notificationPollingInFlight: false,
      numberOfAlarms: 0,
      numberOfDeviceStatuses: 0,
      alarmList: [],
      deviceStatusList: [],
      previousAlarmNotificationIds: new Set(),
      previousDeviceStatusNotificationIds: new Set(),
      isInitialAlarmLoad: true,
      isInitialDeviceStatusLoad: true,
    }
  },
  computed: {
    notificationCount() {
      return this.numberOfAlarms + this.numberOfDeviceStatuses
    },
  },
  created() {
    this.startNotificationPolling()
    document.addEventListener('click', this.handleNotificationClickOutside)
  },
  beforeDestroy() {
    this.stopNotificationPolling()
    document.removeEventListener('click', this.handleNotificationClickOutside)
  },
  methods: {
    toggleNotificationPanel(event) {
      event.stopPropagation()
      this.showNotificationPanel = !this.showNotificationPanel
    },

    handleNotificationClickOutside(event) {
      const target = event.target
      if (
        this.showNotificationPanel &&
        (!target || typeof target.closest !== 'function' || !target.closest('.notification-wrapper'))
      ) {
        this.showNotificationPanel = false
      }
    },

    handleAlarmClick(alarm) {
      this.$router.push({
        path: '/alarmCenter/alarmRecord',
        query: { id: alarm.alarm_id || alarm.id },
      })
      this.showNotificationPanel = false
    },

    handleAlarmProcess(alarm) {
      if (alarm.event_type === ALARM_EVENT_TYPE.RECOVER) {
        this.handleAlarmClick(alarm)
        return
      }
      this.$router.push({
        path: '/alarmCenter/alarmRecord',
        query: {
          id: alarm.alarm_id || alarm.id,
          process: true,
        },
      })
      this.showNotificationPanel = false
    },

    handleDeviceStatusClick(item) {
      this.$router.push({
        path: '/alarmCenter/deviceOnlineRecord',
        query: { device_id: item.device_id },
      })
      this.showNotificationPanel = false
    },

    async handleMarkAllRead(category) {
      const isDeviceStatus = category === NOTIFICATION_CATEGORY.DEVICE_STATUS
      try {
        const response = isDeviceStatus
          ? await markAllDeviceStatusNotificationsRead()
          : await markAllAlarmsRead()
        if (response.code !== 0) {
          this.$message.error(response.msg || response.message || '操作失败')
          return
        }

        const affected = (response.data && response.data.affected) || 0
        this.$message.success(
          affected > 0 ? `已标记 ${affected} 条通知为已读` : '没有新的未读通知',
        )
        if (isDeviceStatus) {
          this.deviceStatusList = []
          this.numberOfDeviceStatuses = 0
          this.previousDeviceStatusNotificationIds = new Set()
        } else {
          this.alarmList = []
          this.numberOfAlarms = 0
          this.previousAlarmNotificationIds = new Set()
        }
      } catch (error) {
        this.$message.error('操作失败')
      }
    },

    showNewAlarmNotification(alarm) {
      const isRecovery = alarm.event_type === ALARM_EVENT_TYPE.RECOVER
      const typeMap = { 1: 'info', 2: 'warning', 3: 'error' }
      const deviceName = this.escapeNotificationHtml(alarm.device_name || '未知设备')
      const eventTime = this.escapeNotificationHtml(alarm.event_time || '-')
      const pointName = this.escapeNotificationHtml(alarm.point_name || '-')
      const eventValue = this.escapeNotificationHtml(this.formatAlarmValue(alarm.event_value))
      const eventDescription = this.escapeNotificationHtml(alarm.event_description || '-')
      const valueLabel = isRecovery ? '恢复值' : '报警值'
      const descriptionLabel = isRecovery ? '恢复描述' : '报警描述'

      const message = `
        <div class="notification-content">
          <div class="notification-header">
            <span class="device-name">${deviceName}</span>
            <span class="time">${eventTime}</span>
          </div>
          <div class="notification-item">
            <span class="label">数据点</span>
            <span class="value">${pointName}</span>
          </div>
          <div class="notification-item">
            <span class="label">${valueLabel}</span>
            <span class="value">${eventValue}</span>
          </div>
          <div class="notification-item">
            <span class="label">${descriptionLabel}</span>
            <span class="value notification-description">${eventDescription}</span>
          </div>
        </div>
      `

      Notification({
        title: isRecovery ? '告警已恢复' : '新报警通知',
        dangerouslyUseHTMLString: true,
        message,
        type: isRecovery ? 'success' : (typeMap[alarm.alarm_type] || 'error'),
        duration: 5000,
        position: 'top-right',
        customClass: 'alarm-notification',
        onClick: () => this.handleAlarmClick(alarm),
      })
    },

    showAggregatedAlarmNotification(alarms) {
      const isRecovery = alarms[0].event_type === ALARM_EVENT_TYPE.RECOVER
      const maxType = alarms.reduce((value, item) => Math.max(value, item.alarm_type || 0), 0)
      const typeMap = { 1: 'info', 2: 'warning', 3: 'error' }
      const preview = alarms.slice(0, 3).map(item => {
        const deviceName = this.escapeNotificationHtml(item.device_name || '未知设备')
        const description = this.escapeNotificationHtml(item.event_description || '-')
        return `<div class="notification-item notification-item--preview"><span class="value">${deviceName}</span><span class="notification-description">${description}</span></div>`
      }).join('')
      const more = alarms.length > 3
        ? `<div class="notification-item"><span class="label">其余</span><span class="value">${alarms.length - 3} 条</span></div>`
        : ''

      Notification({
        title: isRecovery ? `${alarms.length} 条告警已恢复` : `新增 ${alarms.length} 条报警`,
        dangerouslyUseHTMLString: true,
        message: `<div class="notification-content">${preview}${more}</div>`,
        type: isRecovery ? 'success' : (typeMap[maxType] || 'error'),
        duration: 5000,
        position: 'top-right',
        customClass: 'alarm-notification',
        onClick: () => this.$router.push({ path: '/alarmCenter/alarmRecord' }),
      })
    },

    showNewDeviceStatusNotification(item) {
      const isOnline = item.event_type === DEVICE_STATUS_EVENT_TYPE.ONLINE
      const deviceName = this.escapeNotificationHtml(item.device_name || '未知设备')
      const eventTime = this.escapeNotificationHtml(item.event_time || '-')
      const imei = this.escapeNotificationHtml(item.device_imei || '-')
      const statusText = isOnline ? '设备已上线' : '设备已离线'

      Notification({
        title: isOnline ? '设备上线' : '设备离线',
        dangerouslyUseHTMLString: true,
        message: `
          <div class="notification-content">
            <div class="notification-header">
              <span class="device-name">${deviceName}</span>
              <span class="time">${eventTime}</span>
            </div>
            <div class="notification-item">
              <span class="label">状态</span>
              <span class="value">${statusText}</span>
            </div>
            <div class="notification-item">
              <span class="label">IMEI</span>
              <span class="value">${imei}</span>
            </div>
          </div>
        `,
        type: isOnline ? 'success' : 'warning',
        duration: 4000,
        position: 'top-right',
        customClass: 'alarm-notification',
        onClick: () => this.handleDeviceStatusClick(item),
      })
    },

    showAggregatedDeviceStatusNotification(items) {
      const isOnline = items[0].event_type === DEVICE_STATUS_EVENT_TYPE.ONLINE
      const preview = items.slice(0, 3).map(item => (
        `<div class="notification-item notification-item--preview"><span class="value">${this.escapeNotificationHtml(item.device_name || '未知设备')}</span></div>`
      )).join('')
      const more = items.length > 3
        ? `<div class="notification-item"><span class="label">其余</span><span class="value">${items.length - 3} 条</span></div>`
        : ''

      Notification({
        title: `${items.length} 条设备${isOnline ? '上线' : '离线'}通知`,
        dangerouslyUseHTMLString: true,
        message: `<div class="notification-content">${preview}${more}</div>`,
        type: isOnline ? 'success' : 'warning',
        duration: 5000,
        position: 'top-right',
        customClass: 'alarm-notification',
        onClick: () => this.$router.push({ path: '/alarmCenter/deviceOnlineRecord' }),
      })
    },

    normalizeAlarmNotification(alarm, isNew) {
      const notificationId = alarm.notification_id ?? alarm.id
      const alarmId = alarm.alarm_id ?? alarm.id
      const eventType = alarm.event_type || ALARM_EVENT_TYPE.TRIGGER
      const deviceName = alarm.device_name || '未知设备'
      return {
        ...alarm,
        id: notificationId,
        notification_id: notificationId,
        alarm_id: alarmId,
        event_type: eventType,
        event_time: alarm.event_time || alarm.trigger_time || '',
        event_value: alarm.event_value ?? alarm.value,
        event_description: alarm.event_description ?? alarm.alarm_description ?? '',
        alarm_type: alarm.alarm_type || 3,
        device_name: alarm.point_name ? `${deviceName} - ${alarm.point_name}` : deviceName,
        isNew,
      }
    },

    normalizeDeviceStatusNotification(item, isNew) {
      const notificationId = item.notification_id ?? item.id
      return {
        ...item,
        id: notificationId,
        notification_id: notificationId,
        event_time: item.event_time || '',
        device_name: item.device_name || '未知设备',
        device_imei: item.device_imei || '',
        isNew,
      }
    },

    formatAlarmValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : value
    },

    escapeNotificationHtml(value) {
      return String(value ?? '').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character])
    },

    startNotificationPolling() {
      this.checkForNotifications()
      if (this.notificationPollingTimer) {
        clearInterval(this.notificationPollingTimer)
      }
      this.notificationPollingTimer = setInterval(
        () => this.checkForNotifications(),
        POLLING_INTERVAL,
      )
    },

    stopNotificationPolling() {
      if (this.notificationPollingTimer) {
        clearInterval(this.notificationPollingTimer)
        this.notificationPollingTimer = null
      }
    },

    async checkForNotifications() {
      if (this.notificationPollingInFlight) return
      this.notificationPollingInFlight = true
      try {
        const [alarmResult, deviceStatusResult] = await Promise.all([
          settle(getUnreadAlarms()),
          settle(getUnreadDeviceStatusNotifications()),
        ])
        if (alarmResult.response && alarmResult.response.code === 0) {
          this.applyAlarmNotifications(alarmResult.response)
        }
        if (deviceStatusResult.response && deviceStatusResult.response.code === 0) {
          this.applyDeviceStatusNotifications(deviceStatusResult.response)
        }
      } finally {
        this.notificationPollingInFlight = false
      }
    },

    applyAlarmNotifications(response) {
      const alarms = (response.data && response.data.list) ||
        (Array.isArray(response.data) ? response.data : [])
      this.numberOfAlarms = (response.data && response.data.total) ?? alarms.length

      const currentIds = new Set(alarms.map(item => item.notification_id ?? item.id))
      const newIds = new Set(
        this.isInitialAlarmLoad
          ? []
          : [...currentIds].filter(id => !this.previousAlarmNotificationIds.has(id)),
      )
      const normalized = alarms.map(item => {
        const id = item.notification_id ?? item.id
        return this.normalizeAlarmNotification(item, newIds.has(id))
      })

      this.previousAlarmNotificationIds = currentIds
      this.isInitialAlarmLoad = false
      this.alarmList = normalized.slice(0, MAX_VISIBLE_NOTIFICATIONS)

      const newAlarms = normalized.filter(item => item.isNew)
      if (newAlarms.length === 0) return
      const eventGroups = [
        newAlarms.filter(item => item.event_type === ALARM_EVENT_TYPE.TRIGGER),
        newAlarms.filter(item => item.event_type === ALARM_EVENT_TYPE.RECOVER),
      ]
      eventGroups.forEach(group => {
        if (group.length > 3) {
          this.showAggregatedAlarmNotification(group)
        } else {
          group.forEach(item => this.showNewAlarmNotification(item))
        }
      })
      this.clearNewFlags('alarmList', newIds)
    },

    applyDeviceStatusNotifications(response) {
      const items = (response.data && response.data.list) ||
        (Array.isArray(response.data) ? response.data : [])
      this.numberOfDeviceStatuses = (response.data && response.data.total) ?? items.length

      const currentIds = new Set(items.map(item => item.notification_id ?? item.id))
      const newIds = new Set(
        this.isInitialDeviceStatusLoad
          ? []
          : [...currentIds].filter(id => !this.previousDeviceStatusNotificationIds.has(id)),
      )
      const normalized = items.map(item => {
        const id = item.notification_id ?? item.id
        return this.normalizeDeviceStatusNotification(item, newIds.has(id))
      })

      this.previousDeviceStatusNotificationIds = currentIds
      this.isInitialDeviceStatusLoad = false
      this.deviceStatusList = normalized.slice(0, MAX_VISIBLE_NOTIFICATIONS)

      const newItems = normalized.filter(item => item.isNew)
      if (newItems.length === 0) return
      const eventGroups = [
        newItems.filter(item => item.event_type === DEVICE_STATUS_EVENT_TYPE.ONLINE),
        newItems.filter(item => item.event_type === DEVICE_STATUS_EVENT_TYPE.OFFLINE),
      ]
      eventGroups.forEach(group => {
        if (group.length > 3) {
          this.showAggregatedDeviceStatusNotification(group)
        } else {
          group.forEach(item => this.showNewDeviceStatusNotification(item))
        }
      })
      this.clearNewFlags('deviceStatusList', newIds)
    },

    clearNewFlags(listKey, notificationIds) {
      setTimeout(() => {
        this[listKey] = this[listKey].map(item => (
          notificationIds.has(item.notification_id) ? { ...item, isNew: false } : item
        ))
      }, 5000)
    },
  },
}
